<template>
  <div class="timeclock-backdrop" @click.self.stop.prevent>
    <section class="timeclock-card" role="dialog" aria-modal="true" aria-labelledby="timeclock-title">
      <header>
        <div>
          <small>Reloj checador</small>
          <h2 id="timeclock-title">Registrar asistencia</h2>
        </div>
        <button class="close-button" type="button" aria-label="Cerrar" @click="$emit('close')">×</button>
      </header>

      <template v-if="!result">
        <div class="action-grid">
          <button type="button" :class="{ active: action === 'clock_in' }" @click="action = 'clock_in'">
            <span>↪</span>
            <strong>Entrada</strong>
            <small>Inicio de jornada</small>
          </button>
          <button type="button" :class="{ active: action === 'clock_out' }" @click="action = 'clock_out'">
            <span>↩</span>
            <strong>Salida</strong>
            <small>Fin de jornada</small>
          </button>
        </div>

        <label class="pin-label" for="employee-pin">PIN personal</label>
        <input
          id="employee-pin"
          ref="pinInput"
          v-model="pin"
          class="pin-input"
          type="password"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="8"
          autocomplete="off"
          placeholder="••••"
          @input="sanitizePin"
          @keydown.enter.prevent="submit"
        />
        <div class="pin-dots" aria-hidden="true">
          <span v-for="index in 8" :key="index" :class="{ filled: index <= pin.length }"></span>
        </div>

        <div class="keypad" aria-label="Teclado numerico">
          <button v-for="number in ['1','2','3','4','5','6','7','8','9']" :key="number" type="button" @click="append(number)">{{ number }}</button>
          <button type="button" class="muted" @click="pin = ''">Limpiar</button>
          <button type="button" @click="append('0')">0</button>
          <button type="button" class="muted" aria-label="Borrar ultimo digito" @click="pin = pin.slice(0, -1)">⌫</button>
        </div>

        <p v-if="error" class="feedback error">{{ error }}</p>
        <button class="submit-button" type="button" :disabled="loading || pin.length < 4" @click="submit">
          {{ loading ? 'Registrando…' : action === 'clock_in' ? 'Confirmar entrada' : 'Confirmar salida' }}
        </button>
      </template>

      <div v-else class="success-state">
        <span class="success-icon">✓</span>
        <small>{{ result.action === 'clock_in' ? 'Entrada registrada' : 'Salida registrada' }}</small>
        <h3>{{ result.employee?.name }}</h3>
        <p>{{ formatDateTime(result.registered_at) }}</p>
        <strong>{{ Number(result.week?.worked_hours || 0).toFixed(2) }} h esta semana</strong>
        <button class="submit-button" type="button" @click="$emit('close')">Terminar</button>
      </div>
    </section>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'TimeClockModal',
  emits: ['close', 'success'],
  data() {
    return { action: 'clock_in', pin: '', loading: false, error: '', result: null };
  },
  mounted() {
    this.$nextTick(() => this.$refs.pinInput?.focus?.());
  },
  methods: {
    sanitizePin() { this.pin = String(this.pin || '').replace(/\D/g, '').slice(0, 8); },
    append(number) {
      if (this.pin.length < 8) this.pin += number;
      this.$nextTick(() => this.$refs.pinInput?.focus?.());
    },
    async submit() {
      if (this.loading || this.pin.length < 4) return;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await api.post('/api/pos/hr/time-clock/punch', { action: this.action, pin: this.pin });
        this.result = data;
        this.pin = '';
        this.$emit('success', data);
      } catch (error) {
        this.pin = '';
        this.error = error.response?.data?.error || 'No se pudo registrar la checada.';
        this.$nextTick(() => this.$refs.pinInput?.focus?.());
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '';
      return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Mexico_City' }).format(new Date(value));
    },
  },
};
</script>

<style lang="scss" scoped>
.timeclock-backdrop { position: fixed; inset: 0; z-index: 5000; display: grid; place-items: center; padding: 1rem; background: rgba(13, 22, 38, .58); backdrop-filter: blur(8px); }
.timeclock-card { width: min(29rem, 100%); max-height: calc(100vh - 2rem); overflow: auto; border: 1px solid rgba(255,255,255,.16); border-radius: 30px; padding: 1.25rem; color: #fff; background: radial-gradient(circle at 15% 0, rgba(212,132,68,.3), transparent 14rem), linear-gradient(145deg, #17283f, #0c1422); box-shadow: 0 30px 100px rgba(0,0,0,.42); }
header { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; }
header small, .success-state small { color: rgba(255,255,255,.62); font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
h2 { margin: .25rem 0 0; }
.close-button { width: 2.65rem; height: 2.65rem; border: 0; border-radius: 999px; color: #fff; background: rgba(255,255,255,.12); font-size: 1.35rem; cursor: pointer; }
.action-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; margin: 1.2rem 0; }
.action-grid button { display: grid; gap: .18rem; text-align: left; border: 1px solid rgba(255,255,255,.12); border-radius: 20px; padding: .9rem; color: rgba(255,255,255,.72); background: rgba(255,255,255,.06); cursor: pointer; }
.action-grid button.active { border-color: #d9a16f; color: #fff; background: rgba(212,132,68,.24); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); }
.action-grid span { font-size: 1.3rem; }
.action-grid strong { font-size: 1rem; }
.action-grid small { color: inherit; opacity: .68; }
.pin-label { display: block; text-align: center; color: rgba(255,255,255,.68); font-weight: 900; }
.pin-input { position: absolute; opacity: 0; pointer-events: none; }
.pin-dots { display: flex; justify-content: center; gap: .48rem; min-height: 2rem; margin: .75rem 0 1rem; }
.pin-dots span { width: .78rem; height: .78rem; border-radius: 999px; border: 1px solid rgba(255,255,255,.34); background: rgba(255,255,255,.06); }
.pin-dots span.filled { border-color: #ffd5ad; background: #ffd5ad; box-shadow: 0 0 18px rgba(255,194,139,.34); }
.keypad { display: grid; grid-template-columns: repeat(3, 1fr); gap: .55rem; }
.keypad button { min-height: 3.25rem; border: 1px solid rgba(255,255,255,.10); border-radius: 16px; color: #fff; background: rgba(255,255,255,.09); font-size: 1.05rem; font-weight: 900; cursor: pointer; }
.keypad button:active { transform: scale(.97); background: rgba(255,255,255,.16); }
.keypad button.muted { color: rgba(255,255,255,.62); font-size: .78rem; }
.submit-button { width: 100%; border: 0; border-radius: 17px; padding: 1rem; margin-top: .9rem; color: #142137; background: linear-gradient(135deg, #ffd2a5, #e9a56c); font-weight: 1000; cursor: pointer; }
.submit-button:disabled { opacity: .48; cursor: not-allowed; }
.feedback { border-radius: 14px; padding: .75rem; margin: .8rem 0 0; font-weight: 800; }
.feedback.error { color: #ffd0d0; background: rgba(231,111,81,.18); }
.success-state { display: grid; justify-items: center; text-align: center; padding: 1.5rem .5rem .25rem; }
.success-icon { width: 4.5rem; height: 4.5rem; display: grid; place-items: center; margin-bottom: 1rem; border-radius: 999px; color: #0e463c; background: #9ce7d5; font-size: 2.2rem; font-weight: 1000; }
.success-state h3 { margin: .35rem 0; font-size: 1.6rem; }
.success-state p { margin: 0 0 .8rem; color: rgba(255,255,255,.68); }
.success-state > strong { color: #ffd5ad; }
@media (max-width: 420px) { .timeclock-card { padding: 1rem; border-radius: 24px; } .keypad button { min-height: 3rem; } }
</style>
